import {
  ApiChartRankDrection,
  ApiDimension,
  ApiMetric,
  ApiOperator,
} from "@incendium/api";
import { ProductsRanking } from "features/analytics";
import { ExplorerProvider } from "features/explorer/";
import { TExploreConfig } from "features/explorer/types";

export type ProductRankType =
  | "improve"
  | "leading"
  | "promising"
  | "non-sellers"
  | "gateway";

const config: TExploreConfig<ProductRankType> = {
  leading: {
    title: "Leading Products",
    desc: "These products are the most visited, best converting products. They are best used in marketing campaigns and featured on your most prominent landing pages such as your homepage.",
    default: [
      ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_PRODUCT_REVENUE,
    ],
    ranking: [
      {
        ranks: [
          {
            metric: ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_SALES_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
        ],
      },
    ],
    cards: ["improvement", "top", "hot"],
    analyseBy: {
      description: "Select what to split products by",

      grouping: [
        {
          option: "",
          dimensions: [
            ApiDimension.DIMENSION_PRODUCT_NAME,
            ApiDimension.DIMENSION_PRODUCT_CATEGORY,
            ApiDimension.DIMENSION_PRODUCT_BRAND,
          ],
        },
      ],
    },
    having: {
      description: "Exclude products based on Pageviews or Sales.s etc.",
      havings: [
        {
          label: "Min No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Min No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
      ],
    },
  },
  improve: {
    title: "Products to improve",
    desc: "These are your worst performing products; the most visited, but poorest converting. Analyse the product pages and consider changes to copy, pricing or perhaps review the traffic sources as they may be mismatched.",
    default: [
      ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
      ApiMetric.METRIC_PRODUCT_REVENUE,
    ],
    ranking: [
      {
        ranks: [
          {
            metric: ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
            direction: ApiChartRankDrection.DESC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_SALES_REVENUE,
            direction: ApiChartRankDrection.DESC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_REVENUE,
            direction: ApiChartRankDrection.DESC,
            weight: 0.1,
          },
        ],
      },
    ],
    cards: ["regression", "bottom", "cold"],
    having: {
      description: "Exclude products based on Pageviews or Sales.s etc.",
      havings: [
        {
          label: "Min No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Min No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
      ],
    },
    analyseBy: {
      description: "Select what to split products by",
      grouping: [
        {
          option: "",
          dimensions: [
            ApiDimension.DIMENSION_PRODUCT_NAME,
            ApiDimension.DIMENSION_PRODUCT_CATEGORY,
            ApiDimension.DIMENSION_PRODUCT_BRAND,
          ],
        },
      ],
    },
  },
  promising: {
    title: "Promising Products",
    desc: "Among your less visited products, these products converted the best during the period. Evaluate why these products have low traffic. Are they niche or just an undermarketed products? Drive more traffic to them using marketing campaigns and highlighting/promotion in positioning in results and you will likely drive more revenue.",
    default: [
      ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
      ApiMetric.METRIC_SALES_REVENUE,
      ApiMetric.METRIC_PRODUCT_REVENUE,
      ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
    ],
    ranking: [
      {
        ranks: [
          {
            metric: ApiMetric.METRIC_PRODUCT_CLICK_THROUGH_RATE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_SALES_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            direction: ApiChartRankDrection.DESC,
            weight: 0.1,
          },
        ],
      },
    ],
    having: {
      description: "Exclude products based on Pageviews or Sales.s etc.",
      havings: [
        {
          label: "Min No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: 20,
        },
        {
          label: "Max No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Min No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
      ],
    },
    cards: ["improvement", "top", "hot"],
    analyseBy: {
      description: "Select what to split products by",
      grouping: [
        {
          option: "",
          dimensions: [
            ApiDimension.DIMENSION_PRODUCT_NAME,
            ApiDimension.DIMENSION_PRODUCT_CATEGORY,
            ApiDimension.DIMENSION_PRODUCT_BRAND,
          ],
        },
      ],
    },
  },
  "non-sellers": {
    title: "Non-sellers",
    desc: "These products are not selling at all. Try making improvements to the product copy, reviews, images, and pricing. Reexamine the products to ensure it is relevant to your customer base. Assess the sources of traffic driving people to them; perhaps the campaigns are not well targeted",
    default: [ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT],
    ranking: [
      {
        ranks: [
          {
            metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
        ],
      },
    ],
    having: {
      description: "Exclude products based on Pageviews or Sales.s etc.",
      havings: [
        {
          label: "Min No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Min No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: 0,
        },
      ],
    },
    cards: ["regression", "bottom", "cold"],
    analyseBy: {
      description: "Select what to split products by",
      grouping: [
        {
          option: "",
          dimensions: [
            ApiDimension.DIMENSION_PRODUCT_NAME,
            ApiDimension.DIMENSION_PRODUCT_CATEGORY,
            ApiDimension.DIMENSION_PRODUCT_BRAND,
          ],
        },
      ],
    },
  },
  gateway: {
    title: "Gateway Products",
    desc: "These are the best products for driving high value customers. Customers who purchased these products as their first product were most likely to purchase again, or spend more. Include them in marketing and win-back campaigns. We normally recommend reviewing this list monthly rather than weekly",
    default: [ApiMetric.METRIC_LTV_LAST], // todo, product gateway LTV
    ranking: [
      {
        ranks: [
          {
            metric: ApiMetric.METRIC_LTV_LAST, // todo, product gateway LTV
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_PRODUCT_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
          {
            metric: ApiMetric.METRIC_SALES_REVENUE,
            direction: ApiChartRankDrection.ASC,
            weight: 0.1,
          },
        ],
      },
    ],
    having: {
      description: "Exclude products based on Pageviews or Sales.s etc.",
      havings: [
        {
          label: "Min No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Max No. of Pageviews",
          metric: ApiMetric.METRIC_PRODUCT_PAGEVIEWS_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
        {
          label: "Min No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_GREATER_THAN_OR_EQUAL,
          value: 1,
        },
        {
          label: "Max No. of Sales",
          metric: ApiMetric.METRIC_SALES_COUNT,
          operator: ApiOperator.IS_LESS_THAN_OR_EQUAL,
          value: undefined,
        },
      ],
    },
    cards: ["ltv", "top", "hot"],
    analyseBy: {
      description: "Select what to split products by",
      grouping: [
        {
          option: "",
          dimensions: [
            ApiDimension.DIMENSION_PRODUCT_NAME,
            ApiDimension.DIMENSION_PRODUCT_CATEGORY,
            ApiDimension.DIMENSION_PRODUCT_BRAND,
          ],
        },
      ],
    },
  },
};

function ProductsRankingPage() {
  return (
    <>
      <ExplorerProvider configs={config} type={"leading"}>
        <ProductsRanking />
      </ExplorerProvider>
    </>
  );
}

export default ProductsRankingPage;
